import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWebRTC } from '../context/WebRTCContext';
import { useAIMode } from '../context/AIModeContext';
import { useModeSwitch } from '../hooks/useModeSwitch';
import { useAdvisorQueue } from '../context/AdvisorQueueContext';
import { useAutonomyConsole } from '../hooks/useAutonomyConsole';
import { SPECTRUM_LIST } from '../constants/aiModes';
import { deriveAiNowText } from '../constants/aiConfig';
import './AIControlBar.css';

/**
 * AIControlBar — the always-present, glanceable AI status strip in the top bar.
 *
 * This used to be a second full settings panel that duplicated the entire AI
 * Cockpit and kept its own copy of the mode (which drifted). It is now a thin
 * status surface: the current posture, a plain-language read of what the AI is
 * doing, and the count of actions awaiting approval. Clicking the mode opens a
 * compact posture switcher; all real configuration lives in the Cockpit.
 *
 * Mode comes from AIModeContext — the single source of truth — so this strip
 * and the Cockpit can never disagree.
 */
const AIControlBar = () => {
    const { isConnected, featureManager } = useWebRTC();
    const { mode, meta } = useAIMode();
    const switchMode = useModeSwitch();
    const advisor = useAdvisorQueue();
    const autonomy = useAutonomyConsole();
    const autonomyPaused = !!autonomy.config?.paused;

    const [open, setOpen] = useState(false);
    const [aiConfigured, setAiConfigured] = useState(true);
    const panelRef = useRef(null);

    // API-key status → surface a warning badge when the AI can't actually run.
    useEffect(() => {
        if (!isConnected || !featureManager) return;
        const aiConfigFeature = featureManager.getFeature('ai-config');
        if (!aiConfigFeature) return;
        setAiConfigured(aiConfigFeature.getIsConfigured?.() ?? true);
        aiConfigFeature.getConfig?.().catch(() => { /* ignore */ });
        const unsub = aiConfigFeature.subscribeToConfig?.((config) => setAiConfigured(config.isConfigured));
        return () => { unsub?.(); };
    }, [isConnected, featureManager]);

    // Close the posture popover on outside click.
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const handleModeChange = useCallback(async (modeId) => {
        // Entering Acting is confirmed first (see useModeSwitch). Keep the
        // popover open if the user cancels so the current mode stays visible.
        const applied = await switchMode(modeId);
        if (applied) setOpen(false);
    }, [switchMode]);

    const isActing = mode === 'acting';

    if (!isConnected) return null;

    // Plain-language "what is it doing right now" — the SHARED derivation, so
    // this strip and the Cockpit's Now/Next panel can never contradict.
    const nowText = deriveAiNowText({
        mode,
        paused: autonomyPaused,
        advisorCount: advisor.count,
        advisorTopTitle: advisor.pending?.[0]?.title,
    });

    return (
        <div className={`ai-inline-pills${isActing ? ' ai-inline-pills--acting' : ''}`} ref={panelRef} onClick={(e) => e.stopPropagation()}>
            <div className="ai-inline-trigger" data-tooltip={`AI mode: ${meta.label} — ${meta.desc}`}>
                <button
                    className={`ai-strip-mode as-button${isActing ? ' ai-strip-mode--acting' : ''}`}
                    style={{ '--mode-color': meta.color }}
                    onClick={() => setOpen(o => !o)}
                    data-tooltip="Change AI mode"
                >
                    <span className="ai-strip-dot" />
                    <span className="ai-strip-mode-text">{meta.label}</span>
                    {isActing && <span className="ai-strip-live" data-tooltip="The AI is acting on its own, without approval">LIVE</span>}
                    <span className={`ai-strip-caret ${open ? 'open' : ''}`}>▾</span>
                </button>

                <span className="ai-strip-sep" />
                <span className="ai-strip-now" title={nowText}>{nowText}</span>

                {advisor.count > 0 && (
                    <Link
                        to="/app/ai/cockpit"
                        className="ai-strip-badge advisor"
                        data-tooltip={`${advisor.count} AI suggestions awaiting approval — open the Cockpit`}
                    >
                        {advisor.count} to approve
                    </Link>
                )}
                {!aiConfigured && (
                    <span className="ai-strip-badge warn" data-tooltip="API key not configured">No API key</span>
                )}

                <Link
                    to="/app/ai/cockpit"
                    className="ai-strip-cockpit"
                    data-tooltip="Open the AI Cockpit"
                    onClick={() => setOpen(false)}
                >
                    <i className="iconsminds-brain" />
                </Link>
            </div>

            {open && (
                <div className="ai-popover compact">
                    <div className="ai-pop-modes-list">
                        {SPECTRUM_LIST.map(m => (
                            <button
                                key={m.id}
                                className={`ai-pop-mode-row ${mode === m.id ? 'active' : ''}`}
                                style={{ '--mc': m.color }}
                                onClick={() => handleModeChange(m.id)}
                            >
                                <span className="ai-pop-mode-dot" />
                                <span className="ai-pop-mode-row-body">
                                    <span className="ai-pop-mode-name">{m.label}</span>
                                    <span className="ai-pop-mode-desc">{m.desc}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                    <Link
                        to="/app/ai/cockpit"
                        className="ai-pop-cockpit-link"
                        onClick={() => setOpen(false)}
                        data-tooltip="Open the full AI Cockpit page"
                    >
                        <i className="iconsminds-brain" />
                        <span>Open AI Cockpit</span>
                    </Link>
                </div>
            )}
        </div>
    );
};

export default AIControlBar;
